import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Mail,
  Lock,
  KeyRound,
  Loader2,
  Copy,
  Check,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

export function extractApiError(err, fallback = 'Request failed') {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    return detail.message || detail.error || detail.code || fallback;
  }
  return fallback;
}

export function extractApiErrorCode(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') return detail.code || null;
  return null;
}

const AuthenticationSettingsPage = () => {
  const { token, user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  const [emailForm, setEmailForm] = useState({ current_password: '', new_email: '' });
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  const [setupData, setSetupData] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [disableForm, setDisableForm] = useState({ code: '', current_password: '' });
  const [disableLoading, setDisableLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadStatus = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/owner-console/security/authentication`, { headers });
      setStatus(res.data);
      if (res.data?.email) {
        setEmailForm((prev) => ({ ...prev, new_email: res.data.email }));
      }
    } catch (err) {
      setError(extractApiError(err, 'Failed to load authentication settings'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    setEmailMessage('');
    try {
      const res = await axios.post(
        `${API}/owner-console/security/change-email`,
        emailForm,
        { headers }
      );
      setEmailMessage(res.data?.message || 'Email updated');
      toast.success(res.data?.message || 'Email updated');
      setEmailForm((prev) => ({ ...prev, current_password: '' }));
      await loadStatus();
      if (checkAuth) await checkAuth();
    } catch (err) {
      const message = extractApiError(err, 'Failed to change email');
      setEmailMessage(message);
      toast.error(message);
    } finally {
      setEmailSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMessage('New password and confirmation do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await axios.post(
        `${API}/owner-console/security/change-password`,
        passwordForm,
        { headers }
      );
      setPasswordMessage(res.data?.message || 'Password updated');
      toast.success(res.data?.message || 'Password updated');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const message = extractApiError(err, 'Failed to change password');
      setPasswordMessage(message);
      toast.error(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSetup2fa = async () => {
    setSetupLoading(true);
    try {
      const res = await axios.post(`${API}/owner-console/security/2fa/setup`, {}, { headers });
      setSetupData(res.data);
      setSetupCode('');
      toast.success('Scan the QR code, then enter a verification code');
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to start 2FA setup'));
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setVerifyLoading(true);
    try {
      await axios.post(
        `${API}/owner-console/security/2fa/verify`,
        { code: setupCode },
        { headers }
      );
      toast.success('Two-factor authentication enabled');
      setSetupData(null);
      setSetupCode('');
      await loadStatus();
    } catch (err) {
      toast.error(extractApiError(err, 'Invalid verification code'));
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisable2fa = async (e) => {
    e.preventDefault();
    setDisableLoading(true);
    try {
      await axios.post(
        `${API}/owner-console/security/2fa/disable`,
        disableForm,
        { headers }
      );
      toast.success('Two-factor authentication disabled');
      setDisableForm({ code: '', current_password: '' });
      setSetupData(null);
      await loadStatus();
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to disable 2FA'));
    } finally {
      setDisableLoading(false);
    }
  };

  const handleReset2fa = async () => {
    setSetupLoading(true);
    try {
      const res = await axios.post(
        `${API}/owner-console/security/2fa/reset`,
        disableForm,
        { headers }
      );
      setSetupData(res.data);
      setSetupCode('');
      toast.success('2FA reset — scan the new QR code and verify');
      await loadStatus();
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to reset 2FA'));
    } finally {
      setSetupLoading(false);
    }
  };

  const copySecret = async () => {
    if (!setupData?.secret) return;
    await navigator.clipboard.writeText(setupData.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="authentication-loading">
        <Loader2 className="animate-spin text-amber-400" size={32} />
      </div>
    );
  }

  const twoFaEnabled = Boolean(status?.two_factor?.enabled);

  return (
    <div className="min-h-screen bg-[#0B1F18] text-white p-8" data-testid="authentication-settings-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="mb-2">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Shield className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-white">Authentication</h1>
              <p className="text-[#93AE9F] text-sm">
                Manage Platform Owner email, password, and two-factor authentication
              </p>
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-200" role="alert">
            {error}
          </div>
        ) : null}

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="auth-admin-account-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-400" />
              Admin account
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Current email: <span className="text-white">{status?.email || user?.email || '—'}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangeEmail} className="space-y-4">
              <div>
                <Label htmlFor="new-email">New email</Label>
                <Input
                  id="new-email"
                  type="email"
                  className="mt-1.5 bg-zinc-950 border-zinc-700"
                  value={emailForm.new_email}
                  onChange={(e) => setEmailForm((p) => ({ ...p, new_email: e.target.value }))}
                  data-testid="auth-new-email"
                />
              </div>
              <div>
                <Label htmlFor="email-current-password">Current password</Label>
                <Input
                  id="email-current-password"
                  type="password"
                  className="mt-1.5 bg-zinc-950 border-zinc-700"
                  value={emailForm.current_password}
                  onChange={(e) => setEmailForm((p) => ({ ...p, current_password: e.target.value }))}
                  data-testid="auth-email-current-password"
                />
              </div>
              {emailMessage ? (
                <p className="text-sm text-zinc-300" data-testid="auth-email-message">{emailMessage}</p>
              ) : null}
              <Button type="submit" disabled={emailSaving} data-testid="auth-change-email-submit">
                {emailSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Update email
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="auth-password-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-400" />
              Password
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Change your Platform Owner password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="pw-current">Current password</Label>
                <Input
                  id="pw-current"
                  type="password"
                  className="mt-1.5 bg-zinc-950 border-zinc-700"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm((p) => ({ ...p, current_password: e.target.value }))}
                  data-testid="auth-password-current"
                />
              </div>
              <div>
                <Label htmlFor="pw-new">New password</Label>
                <Input
                  id="pw-new"
                  type="password"
                  className="mt-1.5 bg-zinc-950 border-zinc-700"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm((p) => ({ ...p, new_password: e.target.value }))}
                  data-testid="auth-password-new"
                />
              </div>
              <div>
                <Label htmlFor="pw-confirm">Confirm password</Label>
                <Input
                  id="pw-confirm"
                  type="password"
                  className="mt-1.5 bg-zinc-950 border-zinc-700"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm((p) => ({ ...p, confirm_password: e.target.value }))}
                  data-testid="auth-password-confirm"
                />
              </div>
              {passwordMessage ? (
                <p className="text-sm text-zinc-300" data-testid="auth-password-message">{passwordMessage}</p>
              ) : null}
              <Button type="submit" disabled={passwordSaving} data-testid="auth-change-password-submit">
                {passwordSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save password
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="auth-2fa-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-white flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-amber-400" />
                Two-Factor Authentication
              </CardTitle>
              <Badge
                className={
                  twoFaEnabled
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                    : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                }
                data-testid="auth-2fa-status-badge"
              >
                {twoFaEnabled ? 'Enabled' : 'Not enabled'}
              </Badge>
            </div>
            <CardDescription className="text-zinc-400">
              Required for immediate agency/user deletion and other high-risk Admin Control actions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center gap-3">
              {twoFaEnabled ? (
                <ShieldCheck className="text-emerald-400" size={28} />
              ) : (
                <ShieldOff className="text-slate-500" size={28} />
              )}
              <div>
                <p className="font-medium text-white" data-testid="auth-2fa-status-label">
                  {twoFaEnabled ? '2FA enabled' : '2FA not enabled'}
                </p>
                <p className="text-sm text-zinc-400">
                  {twoFaEnabled
                    ? 'Your account can authorize high-risk actions with a TOTP code.'
                    : 'Set up 2FA before using Delete Immediately or other destructive admin actions.'}
                </p>
              </div>
            </div>

            {!twoFaEnabled && !setupData ? (
              <Button onClick={handleSetup2fa} disabled={setupLoading} data-testid="auth-2fa-setup-button">
                {setupLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Set up 2FA
              </Button>
            ) : null}

            {setupData ? (
              <div className="space-y-4 rounded-xl border border-zinc-700 bg-zinc-950/60 p-4" data-testid="auth-2fa-setup-panel">
                {setupData.qr_code ? (
                  <img
                    src={setupData.qr_code}
                    alt="2FA QR code"
                    className="mx-auto h-48 w-48 rounded-lg bg-white p-2"
                    data-testid="auth-2fa-qr"
                  />
                ) : null}
                <div className="flex items-center justify-between gap-2 rounded-lg bg-zinc-900 px-3 py-2">
                  <code className="text-sm text-amber-200 break-all" data-testid="auth-2fa-secret">
                    {setupData.secret}
                  </code>
                  <Button type="button" variant="ghost" size="sm" onClick={copySecret}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                </div>
                <form onSubmit={handleVerify2fa} className="space-y-3">
                  <Label htmlFor="setup-code">Enter 6-digit code</Label>
                  <Input
                    id="setup-code"
                    inputMode="numeric"
                    maxLength={6}
                    className="bg-zinc-950 border-zinc-700 tracking-widest"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    data-testid="auth-2fa-verify-input"
                  />
                  <Button type="submit" disabled={verifyLoading || setupCode.length !== 6} data-testid="auth-2fa-verify-submit">
                    {verifyLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Verify and enable
                  </Button>
                </form>
              </div>
            ) : null}

            {twoFaEnabled ? (
              <div className="space-y-4">
                <form onSubmit={handleDisable2fa} className="space-y-3 rounded-xl border border-zinc-700 p-4">
                  <p className="text-sm text-zinc-400">Disable or reset requires your current password or a valid 2FA code.</p>
                  <Input
                    type="password"
                    placeholder="Current password"
                    className="bg-zinc-950 border-zinc-700"
                    value={disableForm.current_password}
                    onChange={(e) => setDisableForm((p) => ({ ...p, current_password: e.target.value }))}
                    data-testid="auth-2fa-disable-password"
                  />
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="2FA code"
                    className="bg-zinc-950 border-zinc-700"
                    value={disableForm.code}
                    onChange={(e) => setDisableForm((p) => ({ ...p, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    data-testid="auth-2fa-disable-code"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={disableLoading}
                      data-testid="auth-2fa-disable-button"
                    >
                      Disable 2FA
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleReset2fa}
                      disabled={setupLoading}
                      data-testid="auth-2fa-reset-button"
                    >
                      Reset 2FA
                    </Button>
                  </div>
                </form>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthenticationSettingsPage;
